import * as React from 'react'
import { useEffect } from 'react'
import {
  useInvokeApi,
  usePageNotifications,
  useAuth,
  useAgencyApi,
} from '../../hooks'
import { Formik } from 'formik'
import { EditModalActionBar } from '..'
import { Guid, LicensedAgencyConfigurationV1 } from '../../api'
import {
  makeStyles,
  Theme,
  createStyles,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { AsyncPayload } from '../base'
import { Grid, Form, Box, Loader } from 'cs-kevlar-react'
import { FormikTextField } from '../FormikTextField'
import * as Yup from 'yup'

interface EditDeviceConfigurationProps {
  onClose?: () => void
  licensedAgencyId: Guid
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
  }),
)

export const modelSchema = Yup.object().shape({
  deviceSessionLockTimeoutInMinutes: Yup.number()
    .integer('The value must be an integer.')
    .typeError('The value must be an integer.')
    .nullable()
    .min(30, 'The value must be at least 30.'),
})

export const EditDeviceConfiguration: React.FunctionComponent<EditDeviceConfigurationProps> = ({
  onClose,
  licensedAgencyId,
}) => {
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const { dispatch: dispatchNotification } = usePageNotifications()

  const {
    getLicensedAgencyConfiguration,
    editLicensedAgencyConfiguration,
  } = useAgencyApi()

  const [
    licensedAgencyConfiguration,
    setLicensedAgencyConfiguration,
  ] = React.useState<AsyncPayload<LicensedAgencyConfigurationV1>>({
    payload: null,
    isLoading: true,
  })

  if (authUser === null) return null

  const onEdit = (config: LicensedAgencyConfigurationV1, actions: any) => {
    invokeApi(
      () => {
        return editLicensedAgencyConfiguration(licensedAgencyId, config)
      },
      'There was an error updating the configuration.',
      undefined,
      () => {
        dispatchNotification({
          type: 'SetSuccess',
          payload: `Updated device configuration.`,
        })

        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  useEffect(() => {
    const loadData = async () => {
      await invokeApi(
        () => {
          return getLicensedAgencyConfiguration(licensedAgencyId)
        },
        'There was an error getting the configuration.',
        (result) => {
          setLicensedAgencyConfiguration({
            payload: result,
            isLoading: false,
          })
        },
      )
    }

    loadData()
  }, [])

  return (
    <React.Fragment>
      {licensedAgencyConfiguration.isLoading && (
        <Loader size="sm" text="Loading Configuration" />
      )}
      {licensedAgencyConfiguration.payload !== null && (
        <Formik
          initialValues={licensedAgencyConfiguration.payload}
          validationSchema={modelSchema}
          onSubmit={onEdit}
        >
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid container direction="column">
                  <Grid item>
                    <FormikTextField
                      id="deviceSessionLockTimeoutInMinutes"
                      formikProps={formikProps}
                      label="Session Lock Timeout (30 minutes minimum)"
                      fieldName="deviceSessionLockTimeoutInMinutes"
                      fullWidth
                    />
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              {
                <EditModalActionBar
                  canDelete={false}
                  onCancel={onClose}
                  formikProps={formikProps}
                  loader={
                    <Loader size="sm" text="Saving" disableVerticalGutters />
                  }
                />
              }
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
